import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import NoSsr from '@material-ui/core/NoSsr';
import Paper from '@material-ui/core/Paper';
import { useTheme, withStyles } from '@material-ui/core/styles';
import TextField, { BaseTextFieldProps } from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CancelIcon from '@material-ui/icons/Cancel';
import classNames from 'classnames';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { HTMLAttributes } from 'react';
import Select, {
  components,
  MultiValueProps,
  OptionProps,
  PlaceholderProps,
  SingleValueProps,
  ValueContainerProps,
} from 'react-select';
import { NoticeProps } from 'react-select/src/components/Menu';

function NoOptionsMessage(props: NoticeProps<OptionType>) {
  return (
    <Typography color="textSecondary" className={props.selectProps.classes.noOptionsMessage} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

NoOptionsMessage.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired,
} as any;

type InputComponentProps = Pick<BaseTextFieldProps, 'inputRef'> & HTMLAttributes<HTMLDivElement>;

function inputComponent({ inputRef, ...props }: InputComponentProps) {
  return <div ref={inputRef} {...props} />;
}

inputComponent.propTypes = {
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
} as any;

function Control(props: any) {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: { classes, TextFieldProps },
  } = props;

  return (
    <TextField
      fullWidth
      error={props.selectProps.error}
      helperText={props.selectProps.errorMessage}
      required={props.selectProps.required}
      InputLabelProps={{
        root: props.label,
      }}
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          'data-testid': props.selectProps.TextFieldProps.label,
          children,
          ...innerProps,
        },
      }}
      {...TextFieldProps}
    />
  );
}

Control.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  selectProps: PropTypes.object.isRequired,
} as any;

interface OptionType {
  label: string;
  value: string;
}

function Option(props: OptionProps<OptionType>) {
  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      className={props.selectProps.classes.menuItem}
      {...props.innerProps}
      data-testid={props.children}
    >
      {props.children}
    </MenuItem>
  );
}

Option.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool,
} as any;

function Placeholder(props: PlaceholderProps<OptionType>) {
  return (
    <Typography color="textSecondary" className={props.selectProps.classes.placeholder} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

Placeholder.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired,
} as any;

function SingleValue(props: SingleValueProps<OptionType>) {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

SingleValue.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired,
} as any;

function ValueContainer(props: ValueContainerProps<OptionType>) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

ValueContainer.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.object.isRequired,
} as any;

function MultiValue(props: MultiValueProps<OptionType>) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={clsx(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      data-testid={props.children + '_Close'}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

MultiValue.propTypes = {
  children: PropTypes.node,
  isFocused: PropTypes.bool,
  removeProps: PropTypes.object.isRequired,
  selectProps: PropTypes.object.isRequired,
} as any;

function Menu(props: any) {
  return (
    <Paper elevation={2} square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}

function MenuList(menuListProps: any) {
  return (
    <components.MenuList {...menuListProps}>
      <div id="menu-list">{menuListProps.children}</div>
    </components.MenuList>
  );
}

const customComponents = {
  Control,
  Menu,
  MenuList,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

function sortOptions(inputOptions: any) {
  return _.sortBy(inputOptions, (obj) => obj.label.toLowerCase());
}

function TnSelect(props: any) {
  const { classes } = props;
  const theme = useTheme();
  let options: Array<any> = [];
  if (props.supressNone) {
    options = [
      {
        label: 'N/A',
        value: 'N/A',
      },
    ].concat(props.options);
  } else if (!props.skipSort) {
    options = sortOptions(props.options);
  } else {
    options = props.options;
  }

  return (
    <div
      className={classNames({
        [classes.root]: true,
        [classes.rootDisabled]: props.disabled,
        [classes.noMinHeight]: props.noMinHeight,
      })}
      // className={
      //   ClassNames({
      //     [classes.root]: true,
      //     [classes.smallInput]: variant === "small",
      //     [classes.largeInput]: variant === 'large',
      //     [classes.mediumInput]: variant === undefined
      //   })
      // }
    >
      <NoSsr>
        <Select
          classes={classes}
          styles={customStyles(theme)}
          inputId={props.id}
          TextFieldProps={{
            label: props.label,
            InputLabelProps: {
              shrink: true,
              className: classes.label,
            },
            placeholder: props.placeholder,
          }}
          inputProps={{
            'data-testid': props.label,
          }}
          isDisabled={props.disabled}
          options={options}
          components={customComponents}
          value={props.value}
          required={props.required}
          onChange={props.onChange}
          isMulti={props.isMulti}
          error={props.error}
          errorMessage={props.errorMessage}
          menuPortalTarget={document.body}
          menuShouldScrollIntoView={false}
          closeMenuOnScroll={(e) => {
            const eventTarget = e.target as HTMLElement;
            const child = eventTarget.children?.[0] as HTMLElement;

            return child?.id !== 'menu-list';
          }}
        />
      </NoSsr>
    </div>
  );
}

export default withStyles(() => ({
  root: {
    flexGrow: 1,
    minHeight: '76px',
    marginTop: 9,
    marginBottom: 8,
  },
  rootDisabled: {
    opacity: 0.5,
  },
  noMinHeight: {
    minHeight: '0 !important',
  },
  input: {
    display: 'flex',
    padding: 0,
    height: 'auto',
  },

  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
    paddingBottom: '10px',
    paddingLeft: '12px',
    paddingTop: '10px',
  },
  chip: {
    margin: 0.5,
  },
  noOptionsMessage: {
    padding: 10,
  },
  singleValue: {
    fontSize: 16,
    color: '#1C1E20',
    fontFamily: 'Avenir',
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    bottom: 6,
    fontSize: 16,
    paddingBottom: '6px',
    paddingLeft: '12px',
    paddingTop: '10px',
  },
  paper: {
    position: 'absolute',
    zIndex: 1000,
    left: 0,
    right: 0,
    marginTop: 5,
  },
  label: {
    fontSize: '14px !important',
    paddingLeft: '12px',
    paddingTop: '8px',
  },
  menuItem: {
    color: '#1C1E20',
    fontSize: 14,
    '&:hover': {
      backgroundColor: '#e7f5ff !important',
    },
    '&:focus': {
      backgroundColor: '#e7f5ff !important',
    },
  },
}))(TnSelect);

const customStyles = (theme: any, error?: boolean) => ({
  control: (provided: any, state: { isFocused: boolean }) => ({
    ...provided,
    width: '100%',
    minHeight: '80px', //
    borderWidth: '1px',
    borderColor: error ? 'red' : state.isFocused ? theme.palette.primary.main : '#E0E0E0',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
    borderRadius: 4,
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    display: 'flex',
    alignItems: 'center',
    fontSize: 16,
    height: '100%',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    marginLeft: 2,
    marginRight: 2,
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    alignItems: 'center',
  }),
  input: (provided: any) => ({
    ...provided,
    '& input': {
      font: 'inherit',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    color: theme.palette.primary.main,
  }),
  menu: (provided: any) => ({
    ...provided,
    width: '100%',
    padding: 0,
  }),
  menuList: (provided: any) => ({
    ...provided,
    overflowX: 'hidden',
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 99999,
  }),
  option: (provided: any) => ({
    ...provided,
    color: 'inherit',
    padding: '6px 0px 6px 0px',
  }),
});
